import React from 'react';
import { Crown, Zap } from 'lucide-react';

interface SubscriptionBadgeProps {
  subscriptionStatus?: string;
  planType?: string;
  trialEndsAt?: string | null;
  theme: 'light' | 'dark';
  size?: 'sm' | 'md' | 'lg';
}

export const SubscriptionBadge: React.FC<SubscriptionBadgeProps> = ({ 
  subscriptionStatus, 
  planType,
  theme,
  size = 'md'
}) => {
  // Debug logging to check production values
  console.log('SubscriptionBadge - subscriptionStatus:', subscriptionStatus, 'planType:', planType);

  // Don't show badge if no subscription info or if free plan
  if (!subscriptionStatus || subscriptionStatus === 'inactive' || planType === 'free') {
    console.log('Badge hidden - no subscription or inactive or free');
    return null;
  }

  const isTrialing = subscriptionStatus === 'trial' || subscriptionStatus === 'trialing';
  const isActive = subscriptionStatus === 'active';
  const isPro = planType === 'pro' || planType === 'premium' || planType === 'standard' || planType === 'basic';
  
  console.log('Badge conditions - isTrialing:', isTrialing, 'isActive:', isActive, 'isPro:', isPro);

  // Size configurations
  const sizeConfig = {
    sm: {
      container: 'px-2 py-0.5 text-[10px]',
      icon: 'h-2.5 w-2.5',
      text: 'text-[10px]',
    },
    md: {
      container: 'px-2.5 py-1 text-xs',
      icon: 'h-3 w-3',
      text: 'text-xs',
    },
    lg: {
      container: 'px-3 py-1.5 text-sm',
      icon: 'h-3.5 w-3.5',
      text: 'text-sm',
    }
  };

  const config = sizeConfig[size];

  // Trial badge (simple solid theme - matching brand blue)
  if (isTrialing) {
    return (
      <div className="relative inline-flex items-center">
        <div className={`
          flex items-center gap-1.5 rounded-full font-semibold tracking-wide
          ${config.container}
          ${theme === 'dark'
            ? 'bg-cyan-600/90 text-white ring-1 ring-cyan-500/50'
            : 'bg-cyan-500 text-white ring-1 ring-cyan-400'
          }
        `}>
          <span className="uppercase font-bold">Trial</span>
          <Zap className={`${config.icon}`} />
        </div>
      </div>
    );
  }

  // PRO badge (simple solid theme)
  if (isActive && isPro) {
    // Determine badge text based on plan type
    const badgeText = planType === 'premium' ? 'PRO' : planType === 'standard' ? 'PRO' : 'PRO';
    
    return (
      <div className="relative inline-flex items-center">
        <div className={`
          flex items-center gap-1.5 rounded-full font-semibold tracking-wide
          ${config.container}
          ${theme === 'dark'
            ? 'bg-blue-600/90 text-white ring-1 ring-blue-500/50'
            : 'bg-blue-600 text-white ring-1 ring-blue-500'
          }
        `}>
          <span className="uppercase font-bold">
            {badgeText}
          </span>
          <Crown className={`${config.icon}`} />
        </div>
      </div>
    );
  }

  return null;
};
